import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Select, Space, Typography } from 'antd';
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import api from '@/services/api.service';
import type { ReportParams } from '@/services/reportDocuments.service';
import ReportDocumentPanel from '@/components/finance/report-document/ReportDocumentPanel';
import PeriodRangeFilter from '@/components/finance/report-document/PeriodRangeFilter';
import { isoDate } from '@/components/finance/report-document/periodPresets';

const { Title, Text } = Typography;

const STATUSES = ['Posted', 'Approved', 'Submitted', 'Draft', 'Cancelled'] as const;

function invoiceStatusLabel(status: string, t: (key: string) => string): string {
  const map: Record<string, string> = {
    Draft: t('statusDraft'),
    Submitted: t('statusSubmitted'),
    Approved: t('statusApproved'),
    Posted: t('statusPosted'),
    Cancelled: t('statusCancelled'),
  };
  return map[status] ?? status;
}

const DirectPurchaseInvoiceRegisterPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('year'), dayjs()]);
  const [status, setStatus] = useState('Posted');
  const [supplierId, setSupplierId] = useState<string | undefined>();
  const [params, setParams] = useState<ReportParams | null>(null);

  const { data: suppliers = [] } = useQuery({
    queryKey: ['statement-suppliers'],
    queryFn: async () => {
      const res = await api.get<any>('/procurement/suppliers');
      const data = (res as any)?.data ?? res;
      return (Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []) as any[];
    },
  });
  const supplierOptions = useMemo(
    () => suppliers.map((s) => ({ value: s.id as string, label: `${s.supplier_name ?? ''}${s.supplier_code ? ` (${s.supplier_code})` : ''}` })),
    [suppliers]
  );

  const generate = () => {
    const next: ReportParams = { from: isoDate(range[0]), to: isoDate(range[1]), status };
    if (supplierId) next.supplier_id = supplierId;
    setParams(next);
  };

  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/direct-purchase-invoices')} style={{ marginBottom: 16 }}>
        {tc('back')}
      </Button>
      <Title level={4}>{t('dpiRegisterTitle')}</Title>
      <ReportDocumentPanel
        reportKey="direct-purchase-invoice-register"
        params={params}
        filters={
          <Space wrap size="middle" align="end">
            <PeriodRangeFilter value={range} onChange={setRange} initialPreset="ytd" />
            <Space direction="vertical" size={4}>
              <Text type="secondary">{t('status')}</Text>
              <Select
                value={status}
                onChange={setStatus}
                style={{ width: 140 }}
                options={[
                  ...STATUSES.map((s) => ({ value: s, label: invoiceStatusLabel(s, t) })),
                  { value: 'all', label: t('allStatuses') },
                ]}
              />
            </Space>
            <Space direction="vertical" size={4}>
              <Text type="secondary">{t('supplier')}</Text>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('allSuppliers')}
                value={supplierId}
                onChange={setSupplierId}
                options={supplierOptions}
                style={{ width: 280 }}
              />
            </Space>
            <Button type="primary" icon={<SearchOutlined />} onClick={generate}>
              {tc('generate')}
            </Button>
          </Space>
        }
      />
    </div>
  );
};

export default DirectPurchaseInvoiceRegisterPage;
